/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";

import { BEM } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";

import { useForkRef } from "../hooks/useForkRef";
import { useDebounce } from "../hooks/useDebounce";

type InputElementType = "input" | "textarea";

export type IInputProps<E extends InputElementType = "input"> = {
	className?: string;
	prefix?: string;
	suffix?: string;
	autoFocus?: boolean;
	autoFocusSelect?: boolean;
	startAdornment?: React.ReactNode;
	endAdornment?: React.ReactNode;
	readOnly?: boolean;
	debounceTimeout?: number;
	multiLine?: E extends "textarea" ? true : false;
	type?: E extends "input" ? React.HTMLProps<E>["type"] : undefined;
	maxRows?: number;
};

export const Input = React.forwardRef(
	<E extends InputElementType, InputElement extends HTMLElementTagNameMap[E] = HTMLElementTagNameMap[E]>(
		{
			className,
			startAdornment,
			endAdornment,
			prefix,
			suffix,
			autoFocus,
			autoFocusSelect,
			value,
			onFocus,
			onBlur,
			readOnly,
			onChange,
			debounceTimeout,
			multiLine,
			type,
			maxRows,
			...inputProps
		}: IInputProps<E> & React.HTMLProps<InputElement>,
		ref: React.Ref<InputElement | null>
	) => {
		const inputRef = useRef<InputElement>();
		const forkRef = useForkRef(inputRef, ref);

		const [ stateValue, setStateValue ] = useState(value);
		const [ onChangeDebounced, isDebouncing ] = useDebounce(onChange, debounceTimeout);

		const handleOnChange = useCallback<React.ChangeEventHandler<InputElement>>(
			(event) => {
				const newValue = event.currentTarget.value;
				setStateValue(newValue);

				onChangeDebounced(event);
			},
			[ onChangeDebounced ]
		);

		const [ focused, setFocused ] = useState(
			inputRef.current && inputRef.current === document?.activeElement
		);

		const baseClass = BEM("edt-input", null, {
			focused,
			"start-adornment": !!startAdornment,
			"end-adornment": !!endAdornment,
			readonly: readOnly,
			multiLine
		});

		const inputValue =
			(prefix || suffix) && !focused ? `${prefix ?? ""}${stateValue ?? ""}${suffix ?? ""}` : stateValue;

		const InputComponent = (multiLine
			? "textarea"
			: "input") as unknown as React.HTMLFactory<InputElement>;

		const inputType: React.HTMLProps<"input">["type"] =
			(prefix || suffix) && type === "number" && !focused ? "text" : type;

		const handleFocus = useCallback<React.FocusEventHandler<InputElement>>(
			(...args) => {
				setFocused(true);

				if (isFunction(onFocus)) {
					onFocus(...args);
				}
			},
			[ setFocused, onFocus ]
		);

		const handleBlur = useCallback<React.FocusEventHandler<InputElement>>(
			(...args) => {
				setFocused(false);

				if (isFunction(onBlur)) {
					onBlur(...args);
				}
			},
			[ setFocused, onBlur ]
		);

		if (className) {
			baseClass.add(className);
		}

		useEffect(() => {
			if (autoFocus) {
				inputRef.current?.focus();

				if (autoFocusSelect) {
					inputRef.current?.setSelectionRange(0, inputRef.current.value?.length);
				}
			}
		}, [ autoFocus, autoFocusSelect ]);

		useEffect(() => {
			if (isDebouncing()) {
				return;
			}

			if (value !== stateValue) {
				setStateValue(value);
			}
		}, [ value, stateValue, isDebouncing ]);

		return (
			<div className={baseClass.toClassName()}>
				{startAdornment}
				<InputComponent
					className={baseClass.element("input").toClassName()}
					ref={forkRef}
					value={inputValue}
					type={inputType}
					onFocus={handleFocus}
					onBlur={handleBlur}
					readOnly={readOnly}
					onChange={handleOnChange}
					rows={maxRows}
					{...inputProps}
				/>
				{endAdornment}
			</div>
		);
	}
);

Input.defaultProps = {
	debounceTimeout: 0
};
